import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { projectsAPI, foldersAPI, filesAPI, requiredDocsAPI, commentsAPI, approvalsAPI, archiveAPI, exportAPI, messagesAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { FilePreview } from '../../components/ui/file-preview';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
  FolderOpen,
  FileText,
  Upload,
  Plus,
  Download,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Trash2,
  FileCheck,
  ClipboardList,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  Archive,
  Send,
  Eye,
} from 'lucide-react';
import { formatDateTime, formatFileSize, getStatusLabel, formatRelativeTime, getApprovalStatusLabel } from '../../lib/utils';

const ProjectDetailPage = () => {
  const { id } = useParams();
  const { user, isStaff } = useAuth();
  const [project, setProject] = useState(null);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [requiredDocs, setRequiredDocs] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileHistory, setFileHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Required documents state
  const [reqDocDialogOpen, setReqDocDialogOpen] = useState(false);
  const [newReqDoc, setNewReqDoc] = useState({ titre: '', description: '' });
  const [uploadingReqDoc, setUploadingReqDoc] = useState(null);

  // Phase 2/3 state: Comments and Approvals
  const [fileDetailDialogOpen, setFileDetailDialogOpen] = useState(false);
  const [fileComments, setFileComments] = useState([]);
  const [fileApprovals, setFileApprovals] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalData, setApprovalData] = useState({ status: '', commentaire: '' });
  const [submittingApproval, setSubmittingApproval] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // File Preview state
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const handlePreviewFile = async (file) => {
    setPreviewFile(file);
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const token = localStorage.getItem('token');
    setPreviewUrl(`${API_URL}/api/files/${file.id}/download?token=${token}`);
    setPreviewDialogOpen(true);
  };

  const fetchData = useCallback(async () => {
    try {
      const [projectRes, foldersRes, filesRes, reqDocsRes] = await Promise.all([
        projectsAPI.getById(id),
        foldersAPI.getByProject(id),
        filesAPI.getByProject(id, currentFolder),
        requiredDocsAPI.getByProject(id),
      ]);
      setProject(projectRes.data);
      setFolders(foldersRes.data);
      setFiles(filesRes.data);
      setRequiredDocs(reqDocsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement du projet');
    } finally {
      setLoading(false);
    }
  }, [id, currentFolder]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFileUpload = async (filesToUpload) => {
    if (!filesToUpload || filesToUpload.length === 0) return;

    setUploading(true);
    try {
      for (const file of filesToUpload) {
        await filesAPI.upload(file, id, currentFolder);
      }
      toast.success(`${filesToUpload.length} fichier(s) téléversé(s) avec succès`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du téléversement');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFileUpload(droppedFiles);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFileUpload(selectedFiles);
    e.target.value = '';
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await foldersAPI.create({
        project_id: id,
        nom: newFolderName,
        dossier_parent_id: currentFolder,
      });
      toast.success('Dossier créé avec succès');
      setFolderDialogOpen(false);
      setNewFolderName('');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création du dossier');
    }
  };

  const handleStatusChange = async (fileId, newStatus) => {
    try {
      await filesAPI.updateStatus(fileId, newStatus);
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce fichier?')) return;
    try {
      await filesAPI.delete(fileId);
      toast.success('Fichier supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleViewHistory = async (file) => {
    setSelectedFile(file);
    setHistoryDialogOpen(true);
    setHistoryLoading(true);
    try {
      const response = await filesAPI.getHistory(file.id);
      setFileHistory(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'historique');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Required Documents handlers
  const handleCreateRequiredDoc = async () => {
    if (!newReqDoc.titre.trim()) return;
    try {
      await requiredDocsAPI.create({
        project_id: id,
        titre: newReqDoc.titre,
        description: newReqDoc.description || null,
      });
      toast.success('Document requis ajouté');
      setReqDocDialogOpen(false);
      setNewReqDoc({ titre: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleUploadRequiredDoc = async (docId, file) => {
    setUploadingReqDoc(docId);
    try {
      await requiredDocsAPI.upload(docId, file);
      toast.success('Document téléversé avec succès');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors du téléversement');
    } finally {
      setUploadingReqDoc(null);
    }
  };

  const handleDeleteRequiredDoc = async (docId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document requis?')) return;
    try {
      await requiredDocsAPI.delete(docId);
      toast.success('Document requis supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Phase 2/3: File Detail (Comments + Approvals)
  const handleViewFileDetail = async (file) => {
    setSelectedFile(file);
    setFileDetailDialogOpen(true);
    try {
      const [commentsRes, approvalsRes] = await Promise.all([
        commentsAPI.getByFile(file.id),
        approvalsAPI.getByFile(file.id),
      ]);
      setFileComments(commentsRes.data);
      setFileApprovals(approvalsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des détails');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedFile) return;
    setSendingComment(true);
    try {
      await commentsAPI.add(selectedFile.id, newComment);
      toast.success('Commentaire ajouté');
      setNewComment('');
      // Refresh comments
      const commentsRes = await commentsAPI.getByFile(selectedFile.id);
      setFileComments(commentsRes.data);
    } catch (error) {
      toast.error('Erreur lors de l\'ajout du commentaire');
    } finally {
      setSendingComment(false);
    }
  };

  const handleSubmitApproval = async () => {
    if (!approvalData.status || !selectedFile) return;
    setSubmittingApproval(true);
    try {
      await approvalsAPI.submit(selectedFile.id, approvalData.status, approvalData.commentaire);
      toast.success('Décision enregistrée');
      setApprovalDialogOpen(false);
      setApprovalData({ status: '', commentaire: '' });
      // Refresh approvals
      const approvalsRes = await approvalsAPI.getByFile(selectedFile.id);
      setFileApprovals(approvalsRes.data);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSubmittingApproval(false);
    }
  };

  const handleArchiveFile = async (fileId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir archiver ce fichier?')) return;
    try {
      await archiveAPI.archiveFile(fileId);
      toast.success('Fichier archivé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const handleExportProject = async () => {
    setExporting(true);
    try {
      const url = exportAPI.getProjectZipUrl(id);
      window.open(url, '_blank');
      toast.success('Export lancé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    } finally {
      setExporting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approuve':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'refuse':
      case 'rejete':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'en_cours_revision':
      case 'revision':
        return <Clock className="w-4 h-4 text-blue-600" />;
      case 'a_completer':
        return <AlertCircle className="w-4 h-4 text-amber-600" />;
      default:
        return <FileCheck className="w-4 h-4 text-primary" />;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'approuve':
        return 'bg-green-100 text-green-800';
      case 'refuse':
      case 'rejete':
        return 'bg-red-100 text-red-800';
      case 'en_cours_revision':
      case 'revision':
        return 'bg-blue-100 text-blue-800';
      case 'a_completer':
        return 'bg-amber-100 text-amber-800';
      case 'recu':
        return 'bg-primary/10 text-primary';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  const currentFolderData = folders.find((f) => f.id === currentFolder);
  const childFolders = folders.filter((f) => f.dossier_parent_id === currentFolder);
  const currentFiles = files.filter((f) => f.dossier_id === currentFolder);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-600">Projet non trouvé.</p>
        <Link to="/portail/projets">
          <Button className="mt-4">Retour aux projets</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/portail/projets">Projets</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{project.nom}</BreadcrumbPage>
          </BreadcrumbItem>
          {currentFolderData && (
            <>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{currentFolderData.nom}</BreadcrumbPage>
              </BreadcrumbItem>
            </>
          )}
        </BreadcrumbList>
      </Breadcrumb>

      {/* Project Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">{project.nom}</h1>
          <p className="text-slate-600">
            {project.client_nom && <span>Client: {project.client_nom}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportProject}
            disabled={exporting}
            data-testid="export-project-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Exporter ZIP
          </Button>
          <Badge variant={project.statut === 'actif' ? 'default' : 'secondary'}>
            {getStatusLabel(project.statut)}
          </Badge>
        </div>
      </div>

      {/* Required Documents Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <ClipboardList className="w-5 h-5" />
            Documents requis
          </CardTitle>
          {isStaff && (
            <Dialog open={reqDocDialogOpen} onOpenChange={setReqDocDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="add-required-doc-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Ajouter un document requis</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Titre du document *</Label>
                    <Input
                      value={newReqDoc.titre}
                      onChange={(e) => setNewReqDoc({ ...newReqDoc, titre: e.target.value })}
                      placeholder="Ex: Certificat d'assurance"
                      data-testid="required-doc-title"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description (optionnel)</Label>
                    <Textarea
                      value={newReqDoc.description}
                      onChange={(e) => setNewReqDoc({ ...newReqDoc, description: e.target.value })}
                      placeholder="Instructions ou détails..."
                      rows={3}
                      data-testid="required-doc-description"
                    />
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={() => setReqDocDialogOpen(false)}>
                      Annuler
                    </Button>
                    <Button onClick={handleCreateRequiredDoc} data-testid="required-doc-submit">
                      Ajouter
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </CardHeader>
        <CardContent>
          {requiredDocs.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>Aucun document requis pour ce projet.</p>
              {isStaff && (
                <p className="text-sm mt-1">Ajoutez des documents que le client doit fournir.</p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {requiredDocs.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200"
                  data-testid={`required-doc-${doc.id}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    doc.statut === 'recu' ? 'bg-green-100' : 'bg-amber-100'
                  }`}>
                    {doc.statut === 'recu' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <Clock className="w-5 h-5 text-amber-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{doc.titre}</p>
                    {doc.description && (
                      <p className="text-sm text-slate-500 line-clamp-1">{doc.description}</p>
                    )}
                    <Badge 
                      variant="secondary" 
                      className={doc.statut === 'recu' ? 'bg-green-100 text-green-800 mt-1' : 'bg-amber-100 text-amber-800 mt-1'}
                    >
                      {doc.statut === 'recu' ? 'Reçu' : 'En attente'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    {doc.statut === 'en_attente' && (
                      <label>
                        <input
                          type="file"
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              handleUploadRequiredDoc(doc.id, e.target.files[0]);
                            }
                            e.target.value = '';
                          }}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          asChild
                          disabled={uploadingReqDoc === doc.id}
                        >
                          <span className="cursor-pointer">
                            {uploadingReqDoc === doc.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-2" />
                                Téléverser
                              </>
                            )}
                          </span>
                        </Button>
                      </label>
                    )}
                    {doc.statut === 'recu' && doc.fichier_id && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${doc.fichier_id}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button variant="outline" size="sm">
                          <Download className="w-4 h-4 mr-2" />
                          Télécharger
                        </Button>
                      </a>
                    )}
                    {isStaff && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteRequiredDoc(doc.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Zone */}
      <Card>
        <CardContent className="p-6">
          <div
            className={`dropzone ${dragActive ? 'active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            data-testid="file-dropzone"
          >
            {uploading ? (
              <div className="flex items-center justify-center gap-3">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
                <span>Téléversement en cours...</span>
              </div>
            ) : (
              <>
                <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                <p className="text-slate-600 mb-2">
                  Glissez-déposez vos fichiers ici ou
                </p>
                <label>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileInput}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                  />
                  <Button variant="outline" asChild>
                    <span className="cursor-pointer">Parcourir</span>
                  </Button>
                </label>
                <p className="text-xs text-slate-400 mt-2">
                  PDF, Word, Excel, Images (max 50 Mo)
                </p>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Navigation & Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          {currentFolder && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentFolder(currentFolderData?.dossier_parent_id || null)}
            >
              ← Retour
            </Button>
          )}
        </div>
        <Dialog open={folderDialogOpen} onOpenChange={setFolderDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" data-testid="create-folder-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau dossier
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nouveau dossier</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nom du dossier</Label>
                <Input
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Nom du dossier"
                  data-testid="folder-name-input"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setFolderDialogOpen(false)}>
                  Annuler
                </Button>
                <Button onClick={handleCreateFolder} data-testid="folder-submit">
                  Créer
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Folders */}
      {childFolders.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {childFolders.map((folder) => (
            <button
              key={folder.id}
              onClick={() => setCurrentFolder(folder.id)}
              className="flex flex-col items-center p-4 bg-white rounded-lg border border-slate-200 hover:border-primary/30 hover:shadow-sm transition-all"
              data-testid={`folder-${folder.id}`}
            >
              <FolderOpen className="w-10 h-10 text-amber-500 mb-2" />
              <span className="text-sm font-medium text-center line-clamp-2">{folder.nom}</span>
            </button>
          ))}
        </div>
      )}

      {/* Files Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">
            Fichiers {currentFolderData && `- ${currentFolderData.nom}`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {currentFiles.length === 0 ? (
            <div className="py-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">Aucun fichier dans ce dossier.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead className="hidden md:table-cell">Taille</TableHead>
                  <TableHead className="hidden lg:table-cell">Version</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="hidden md:table-cell">Date</TableHead>
                  <TableHead className="w-40">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentFiles.map((file) => (
                  <TableRow key={file.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        <div>
                          <p className="font-medium">{file.nom_fichier_original}</p>
                          <p className="text-xs text-slate-500">{file.uploaded_by_name}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {formatFileSize(file.taille_octets)}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      v{file.numero_version}
                    </TableCell>
                    <TableCell>
                      {isStaff ? (
                        <Select
                          value={file.statut}
                          onValueChange={(value) => handleStatusChange(file.id, value)}
                        >
                          <SelectTrigger className="w-36" data-testid={`status-select-${file.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="recu">Reçu</SelectItem>
                            <SelectItem value="en_cours_revision">En cours de révision</SelectItem>
                            <SelectItem value="approuve">Approuvé</SelectItem>
                            <SelectItem value="refuse">Refusé</SelectItem>
                            <SelectItem value="a_completer">À compléter</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge className={getStatusBadgeClass(file.statut)} variant="secondary">
                          <span className="flex items-center gap-1">
                            {getStatusIcon(file.statut)}
                            {getStatusLabel(file.statut)}
                          </span>
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {formatDateTime(file.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePreviewFile(file)}
                          title="Prévisualiser"
                          data-testid={`file-preview-${file.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <a
                          href={filesAPI.getDownloadUrl(file.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex"
                        >
                          <Button variant="ghost" size="sm" title="Télécharger">
                            <Download className="w-4 h-4" />
                          </Button>
                        </a>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewFileDetail(file)}
                          title="Commentaires"
                          data-testid={`file-comments-${file.id}`}
                        >
                          <MessageSquare className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewHistory(file)}
                          title="Historique"
                        >
                          <Clock className="w-4 h-4" />
                        </Button>
                        {isStaff && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleArchiveFile(file.id)}
                              className="text-amber-600 hover:text-amber-700"
                              title="Archiver"
                            >
                              <Archive className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteFile(file.id)}
                              className="text-red-600 hover:text-red-700"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* File Detail Dialog (Comments + Approvals) */}
      <Dialog open={fileDetailDialogOpen} onOpenChange={setFileDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {selectedFile?.nom_fichier_original}
            </DialogTitle>
          </DialogHeader>
          
          {/* Approval Actions (Staff only) */}
          {isStaff && selectedFile && (
            <div className="p-4 bg-slate-50 rounded-lg mb-4">
              <h4 className="font-medium mb-3">Décision d'approbation</h4>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                  onClick={() => {
                    setApprovalData({ status: 'approuve', commentaire: '' });
                    setApprovalDialogOpen(true);
                  }}
                  data-testid="approve-file-btn"
                >
                  <ThumbsUp className="w-4 h-4 mr-2" />
                  Approuver
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
                  onClick={() => {
                    setApprovalData({ status: 'refuse', commentaire: '' });
                    setApprovalDialogOpen(true);
                  }}
                  data-testid="reject-file-btn"
                >
                  <ThumbsDown className="w-4 h-4 mr-2" />
                  Refuser
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"
                  onClick={() => {
                    setApprovalData({ status: 'corrections_demandees', commentaire: '' });
                    setApprovalDialogOpen(true);
                  }}
                  data-testid="request-corrections-btn"
                >
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Demander corrections
                </Button>
              </div>
            </div>
          )}

          {/* Approval History */}
          {fileApprovals.length > 0 && (
            <div className="mb-4">
              <h4 className="font-medium mb-2">Historique des approbations</h4>
              <div className="space-y-2">
                {fileApprovals.map((approval) => (
                  <div
                    key={approval.id}
                    className={`p-3 rounded-lg border ${
                      approval.status === 'approuve' ? 'bg-green-50 border-green-200' :
                      approval.status === 'refuse' ? 'bg-red-50 border-red-200' :
                      'bg-amber-50 border-amber-200'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {approval.status === 'approuve' && <ThumbsUp className="w-4 h-4 text-green-600" />}
                      {approval.status === 'refuse' && <ThumbsDown className="w-4 h-4 text-red-600" />}
                      {approval.status === 'corrections_demandees' && <AlertCircle className="w-4 h-4 text-amber-600" />}
                      <span className="font-medium text-sm">{getApprovalStatusLabel(approval.status)}</span>
                      <span className="text-xs text-slate-500">par {approval.reviewer_nom}</span>
                    </div>
                    {approval.commentaire && (
                      <p className="text-sm text-slate-600">{approval.commentaire}</p>
                    )}
                    <p className="text-xs text-slate-400 mt-1">{formatRelativeTime(approval.created_at)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Comments Section */}
          <div>
            <h4 className="font-medium mb-3">Commentaires ({fileComments.length})</h4>
            
            {/* Add Comment */}
            <div className="flex gap-2 mb-4">
              <Textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Ajouter un commentaire..."
                rows={2}
                className="flex-1"
                data-testid="file-comment-input"
              />
              <Button
                onClick={handleAddComment}
                disabled={!newComment.trim() || sendingComment}
                className="self-end"
                data-testid="submit-comment-btn"
              >
                {sendingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>

            {/* Comments List */}
            {fileComments.length === 0 ? (
              <p className="text-center text-slate-500 py-4">Aucun commentaire.</p>
            ) : (
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {fileComments.map((comment) => (
                  <div key={comment.id} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm">{comment.user_nom}</span>
                      <Badge variant="secondary" className="text-xs">{comment.user_role}</Badge>
                    </div>
                    <p className="text-sm text-slate-600">{comment.contenu}</p>
                    <p className="text-xs text-slate-400 mt-1">{formatRelativeTime(comment.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Approval Confirmation Dialog */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {approvalData.status === 'approuve' && 'Confirmer l\'approbation'}
              {approvalData.status === 'refuse' && 'Confirmer le refus'}
              {approvalData.status === 'corrections_demandees' && 'Demander des corrections'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Commentaire (optionnel)</Label>
              <Textarea
                value={approvalData.commentaire}
                onChange={(e) => setApprovalData({ ...approvalData, commentaire: e.target.value })}
                placeholder="Ajoutez un commentaire..."
                rows={3}
                data-testid="approval-comment"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setApprovalDialogOpen(false)}>
                Annuler
              </Button>
              <Button
                onClick={handleSubmitApproval}
                disabled={submittingApproval}
                className={
                  approvalData.status === 'approuve' ? 'bg-green-600 hover:bg-green-700' :
                  approvalData.status === 'refuse' ? 'bg-red-600 hover:bg-red-700' :
                  'bg-amber-600 hover:bg-amber-700'
                }
                data-testid="confirm-approval-btn"
              >
                {submittingApproval ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Historique - {selectedFile?.nom_fichier_original}</DialogTitle>
          </DialogHeader>
          {historyLoading ? (
            <div className="py-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            </div>
          ) : fileHistory.length === 0 ? (
            <p className="text-slate-500 text-center py-4">Aucun historique</p>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {fileHistory.map((event) => (
                <div key={event.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                    {event.type_evenement === 'televersement' && <Upload className="w-4 h-4 text-green-600" />}
                    {event.type_evenement === 'telechargement' && <Download className="w-4 h-4 text-blue-600" />}
                    {event.type_evenement === 'changement_statut' && <AlertCircle className="w-4 h-4 text-amber-600" />}
                    {event.type_evenement === 'suppression' && <Trash2 className="w-4 h-4 text-red-600" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{event.acteur_nom}</p>
                    <p className="text-xs text-slate-600">
                      {event.type_evenement === 'changement_statut'
                        ? `Statut changé: ${getStatusLabel(event.metadata?.ancien_statut)} → ${getStatusLabel(event.metadata?.nouveau_statut)}`
                        : event.type_evenement}
                    </p>
                    <p className="text-xs text-slate-400">{formatDateTime(event.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* File Preview Dialog */}
      <FilePreview
        file={previewFile}
        open={previewDialogOpen}
        onOpenChange={setPreviewDialogOpen}
        downloadUrl={previewUrl}
      />
    </div>
  );
};

export default ProjectDetailPage;
